import { z } from "zod";
import {
  OpportunityStatusSchema,
  type OpportunityStatus,
  MIN_OPPORTUNITY_PROBABILITY,
  MAX_OPPORTUNITY_PROBABILITY,
  OPPORTUNITY_STATUS_VALUES,
  OPPORTUNITY_STATUS_DISPLAY_NAMES,
} from "./opportunities";

/**
 * The category values for opportunity stages, matching the existing OpportunityStatus enum.
 * Each custom stage belongs to exactly one category.
 */
export const OpportunityStageCategorySchema = OpportunityStatusSchema;
export type OpportunityStageCategory = OpportunityStatus;

/** Standard opportunity fields a stage can require. */
export const OPPORTUNITY_STANDARD_REQUIRED_FIELDS = [
  "companyId",
  "salesRepUserId",
  "estimatorUserIds",
  "amount",
  "locationId",
] as const;
export type OpportunityStandardRequiredField =
  (typeof OPPORTUNITY_STANDARD_REQUIRED_FIELDS)[number];

/** Display labels for standard required fields, shared by the UI, errors and agent hints. */
export const OPPORTUNITY_STANDARD_REQUIRED_FIELD_LABELS: Record<
  OpportunityStandardRequiredField,
  string
> = {
  companyId: "Company",
  salesRepUserId: "Sales Rep",
  estimatorUserIds: "Estimators",
  amount: "Amount",
  locationId: "Location",
};

/** Fields an opportunity must have filled to enter a stage. */
export const OpportunityStageRequiredFieldsSchema = z.object({
  standard: z.array(z.enum(OPPORTUNITY_STANDARD_REQUIRED_FIELDS)),
  custom: z.array(z.string()),
});
export type OpportunityStageRequiredFields = z.infer<
  typeof OpportunityStageRequiredFieldsSchema
>;

/** A stage that requires no fields. */
export const EMPTY_STAGE_REQUIRED_FIELDS: OpportunityStageRequiredFields = {
  standard: [],
  custom: [],
};

/**
 * Compares two stage required-field sets, ignoring order and duplicates.
 * @param a - The first required fields
 * @param b - The second required fields
 * @returns True when both require the same standard and custom fields
 */
export function areStageRequiredFieldsEqual(
  a: OpportunityStageRequiredFields,
  b: OpportunityStageRequiredFields,
): boolean {
  return (
    haveSameMembers(a.standard, b.standard) &&
    haveSameMembers(a.custom, b.custom)
  );
}

/**
 * Checks whether two lists hold the same distinct values.
 * @param a - The first list
 * @param b - The second list
 * @returns True when both lists contain exactly the same distinct values
 */
function haveSameMembers(a: readonly string[], b: readonly string[]): boolean {
  const aValues = new Set(a);
  const bValues = new Set(b);
  return (
    aValues.size === bValues.size &&
    [...aValues].every((value) => bValues.has(value))
  );
}

/** 400 body returned when an opportunity is refused entry to a stage over blank required fields. */
export const OpportunityStageRequirementsErrorSchema = z.object({
  error: z.string(),
  stageName: z.string(),
  missingFields: z.array(z.string()),
});
export type OpportunityStageRequirementsErrorResponse = z.infer<
  typeof OpportunityStageRequirementsErrorSchema
>;

export const OpportunityStageSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  category: OpportunityStageCategorySchema,
  sortOrder: z.number().int(),
  probability: z
    .number()
    .int()
    .min(MIN_OPPORTUNITY_PROBABILITY)
    .max(MAX_OPPORTUNITY_PROBABILITY)
    .nullable(),
  isDefault: z.boolean(),
  requiredFields: OpportunityStageRequiredFieldsSchema,
  archivedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type OpportunityStage = z.infer<typeof OpportunityStageSchema>;

export const OpportunityStageDefinitionSchema = z
  .object({
    id: z.string().min(1).max(255),
    name: z.string().min(1).max(100),
    description: z.string().max(500).nullable(),
    category: OpportunityStageCategorySchema,
    probability: z
      .number()
      .int()
      .min(MIN_OPPORTUNITY_PROBABILITY)
      .max(MAX_OPPORTUNITY_PROBABILITY)
      .nullable(),
    archived: z.boolean(),
    requiredFields: OpportunityStageRequiredFieldsSchema.optional(),
  })
  .strict();
export type OpportunityStageDefinition = z.infer<
  typeof OpportunityStageDefinitionSchema
>;

export const CreateOpportunityStageParamsSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).nullable().optional(),
  category: OpportunityStageCategorySchema,
  sortOrder: z.number().int().nonnegative().optional(),
  probability: z
    .number()
    .int()
    .min(MIN_OPPORTUNITY_PROBABILITY)
    .max(MAX_OPPORTUNITY_PROBABILITY)
    .nullable()
    .optional(),
  requiredFields: OpportunityStageRequiredFieldsSchema.optional(),
});
export type CreateOpportunityStageParams = z.infer<
  typeof CreateOpportunityStageParamsSchema
>;

export const UpdateOpportunityStageParamsSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().max(500).nullable().optional(),
  category: OpportunityStageCategorySchema.optional(),
  sortOrder: z.number().int().nonnegative().optional(),
  probability: z
    .number()
    .int()
    .min(MIN_OPPORTUNITY_PROBABILITY)
    .max(MAX_OPPORTUNITY_PROBABILITY)
    .nullable()
    .optional(),
  archivedAt: z.coerce.date().nullable().optional(),
  requiredFields: OpportunityStageRequiredFieldsSchema.optional(),
});
export type UpdateOpportunityStageParams = z.infer<
  typeof UpdateOpportunityStageParamsSchema
>;

export const ListOpportunityStagesResponseSchema = z.object({
  stages: z.array(OpportunityStageSchema),
});
export type ListOpportunityStagesResponse = z.infer<
  typeof ListOpportunityStagesResponseSchema
>;

export const GetOpportunityStageResponseSchema = z.object({
  stage: OpportunityStageSchema,
});
export type GetOpportunityStageResponse = z.infer<
  typeof GetOpportunityStageResponseSchema
>;

export const CreateOpportunityStageResponseSchema = z.object({
  stage: OpportunityStageSchema,
});
export type CreateOpportunityStageResponse = z.infer<
  typeof CreateOpportunityStageResponseSchema
>;

export const UpdateOpportunityStageResponseSchema = z.object({
  stage: OpportunityStageSchema,
});
export type UpdateOpportunityStageResponse = z.infer<
  typeof UpdateOpportunityStageResponseSchema
>;

/** Terminal category probability overrides: ACCEPTED=100, LOST=0. */
const TERMINAL_CATEGORY_PROBABILITIES: Partial<
  Record<OpportunityStageCategory, number>
> = {
  ACCEPTED: 100,
  LOST: 0,
};

/** Default descriptions for each stage category, used by agents to map situations to stages. */
const DEFAULT_STAGE_DESCRIPTIONS: Record<OpportunityStageCategory, string> = {
  INTAKE:
    "New opportunity that is currently missing information from the submitter.",
  QUOTE_IN_PROGRESS: "Quote is being prepared or revised for the customer.",
  SENT: "Quote has been completed and sent to the customer, awaiting their response.",
  ACCEPTED: "Customer accepted the quote. Deal is closed won.",
  LOST: "Customer declined or chose a competitor. Deal is closed lost.",
};

/**
 * Default stage definitions used to seed an organization's stages on first access.
 * One stage per existing status category, derived from OPPORTUNITY_STATUS_DISPLAY_NAMES.
 */
export const DEFAULT_OPPORTUNITY_STAGES: ReadonlyArray<{
  name: string;
  description: string;
  category: OpportunityStageCategory;
  sortOrder: number;
  probability: number | null;
  isDefault: true;
}> = OPPORTUNITY_STATUS_VALUES.map((category, index) => ({
  name: OPPORTUNITY_STATUS_DISPLAY_NAMES[category],
  description: DEFAULT_STAGE_DESCRIPTIONS[category],
  category,
  sortOrder: index,
  probability: TERMINAL_CATEGORY_PROBABILITIES[category] ?? null,
  isDefault: true as const,
}));
